import {
  Component,
  Input,
  ElementRef,
  ViewChild,
  ChangeDetectorRef,
  OnInit,
  AfterViewInit,
  OnDestroy,
  EventEmitter,
  Output,
  NgZone,
} from '@angular/core';
import { MapView } from '@barthauer/abc/graphics/adapters/open-layers';
import { Subscription } from 'rxjs';
import { ResourceManagerService, ResourceSet } from '@barthauer/abc/core/resources';
import { GeometryHelper } from '../../shared/helpers';
import { trigger, state, style, transition, animate, query, stagger } from '@angular/animations';
import { BasysMapFrameService } from 'libs/basys/map-frame/src/lib/basys-map-frame.service';
import {
  DialogResult,
  MessageBoxButtons,
  MessageBoxIcon,
  MessageBoxService,
  MessageSettings,
} from '@barthauer/abc/messages';
import { ConvertHelper } from '@barthauer/abc/core';
import { Geometry, GeometryEditType } from '../../basys-map-frame.module';

interface FabButton {
  icon: string;
  tooltip: string;
  disabled?: boolean | (() => boolean);
  onClick: () => void;
}

declare var ol: any;

@Component({
  selector: 'barthauer-geometry-editor',
  templateUrl: './geometry-editor.component.html',
  styleUrl: './geometry-editor.component.scss',
  animations: [
    trigger('fabToggler', [
      state('inactive', style({ transform: 'rotate(0deg)' })),
      state('active', style({ transform: 'rotate(90deg)' })),
      transition('inactive <=> active', animate('200ms ease-in')),
    ]),
    trigger('speedDialStagger', [
      transition('* => *', [
        query(
          ':enter',
          [
            style({ opacity: 0, transform: 'scale(0)' }),
            stagger(40, [animate('150ms', style({ opacity: 1, transform: 'scale(1)' }))]),
          ],
          { optional: true },
        ),
      ]),
    ]),
  ],
  standalone: false,
})
export class GeometryEditorComponent implements AfterViewInit, OnInit, OnDestroy {
  @ViewChild('xCoordinateInput', { static: false }) xCoordinateInput: ElementRef;
  @ViewChild('yCoordinateInput', { static: false }) yCoordinateInput: ElementRef;
  @Input() geometryType: GeometryEditType;
  @Input() editGeometry: Geometry;
  @Input() onSave: () => void;
  @Output() closeGeoDialog = new EventEmitter();
  @Output() triggerSave = new EventEmitter<void>();
  @ViewChild('toolTip', { static: false }) toolTip: ElementRef;

  public handleAfterSaveClick: () => void;
  public xCoordinate: string;
  public yCoordinate: string;
  public res = ResourceSet.Empty;
  public fabTogglerState: 'active' | 'inactive' = 'inactive';
  public isLineFinished: boolean = false;
  public isPolygonFinished: boolean = false;
  public isGeoLocationAvailable: boolean = false;
  public showMissingCoordinatesWarning: boolean = false;
  private lineCoordinates: number[][] = [];
  private polygonCoordinates: number[][] = [];

  private mapViews: MapView;
  private handleMapClick: ((event: any) => void) | null = null;
  private isMeasureCoordActive: boolean = false;
  private mapSubscription: Subscription;
  private geoLocationPointFeature: any;
  private geoLocationPointLayer: any;
  private olmap: any;
  private draw: any;
  private vectorSource: any;
  private sketch: any;
  private listener: any;
  private measureTooltipOverlay: any;
  private vectorEditlayer: any = undefined;
  private tooltipCoord: any;
  private lastPointFeature: any = null;
  buttons: FabButton[] = [];

  constructor(
    private resourceService: ResourceManagerService,
    private cd: ChangeDetectorRef,
    private basysMapFrameService: BasysMapFrameService,
    private messageBoxService: MessageBoxService,
    private ngZone: NgZone,
  ) {}

  async ngOnInit() {
    this.res = await this.resourceService.getResourceSet('basys-map-frame');
    this.mapViews = this.basysMapFrameService.map.getCurrentView();
    this.basysMapFrameService.map.enableContextMenu(false);
    const lastGeoLocationSettingEnabled = ConvertHelper.toBoolean(
      localStorage.getItem(BasysMapFrameService.Setting_GeoLocation),
    );
    if (lastGeoLocationSettingEnabled) {
      this.refreshGeoLocationTracking();
    }

    this.isGeoLocationAvailable = lastGeoLocationSettingEnabled;

    this.basysMapFrameService.map.onGeoLocationPositionChanged().subscribe((position) => {
      this.drawGeoLocationPoint(position);
    });

    this.basysMapFrameService.map.onGeoLocationStateChanged().subscribe((state) => {
      this.isGeoLocationAvailable = state.deviceAvailable && state.trackingActive;
      this.setupFabButtons();
      this.cd.detectChanges();
    });

    this.basysMapFrameService.map.onGeoLocationPositionChanged().subscribe();
    this.setupFabButtons();
  }
  private refreshGeoLocationTracking() {
    this.basysMapFrameService.map.stopGeoLocationTracking();
    this.basysMapFrameService.map.startGeoLocationTracking();
  }

  private setupFabButtons(): void {
    const commonButtons: FabButton[] = [
      {
        icon: 'delete',
        tooltip: this.res.get('GeometryEditor-Tooltip-DeleteGeometry'),
        onClick: () => this.deleteGeometries(),
        disabled: () => !this.isGeometryAvailable(),
      },
      {
        icon: 'save',
        tooltip: this.res.get('GeometryEditor-Tooltip-SaveGeometry'),
        onClick: () => this.saveGeometry(),
        disabled: () => !this.isNewGeometryAvailable(),
      },
    ];

    if (this.geometryType === GeometryEditType.Point) {
      this.buttons = [
        ...commonButtons,
        {
          icon: 'gps_fixed',
          tooltip: this.res.get('GeometryEditor-Tooltip-CurrentPosition'),
          onClick: () => this.centerGeoLocation(),
          disabled: () => !this.isGeoLocationAvailable,
        },
      ];
    } else {
      this.buttons = commonButtons;
    }
  }

  isGeometryAvailable(): boolean {
    if (this.geometryType === GeometryEditType.Point) return this.editGeometry?.Points?.length > 0;
    else if (this.geometryType === GeometryEditType.Line) return this.editGeometry?.Lines?.length > 0;
    else if (this.geometryType === GeometryEditType.Polygon) return this.editGeometry?.Polygons?.length > 0;
  }

  isNewGeometryAvailable(): boolean {
    if (this.geometryType === GeometryEditType.Point) return !!this.lastPointFeature;
    else if (this.geometryType === GeometryEditType.Line) return this.lineCoordinates.length >= 2;
    else if (this.geometryType === GeometryEditType.Polygon) return this.polygonCoordinates.length >= 3;
  }

  private updateFabButtonState() {
    this.fabTogglerState = this.isNewGeometryAvailable ? 'active' : 'inactive';
  }

  public isButtonDisabled(btn: FabButton): boolean {
    if (typeof btn.disabled === 'function') {
      return btn.disabled();
    }
    return !!btn.disabled;
  }

  ngAfterViewInit(): void {
    if (this.geometryType === GeometryEditType.Point) {
      this.toggleGeometryType('Point', 'MeasureCoord');
    }
    if (this.geometryType === GeometryEditType.Line) {
      this.toggleGeometryType('LineString', 'MeasureCoord');
    }
    if (this.geometryType === GeometryEditType.Polygon) {
      this.toggleGeometryType('Polygon', 'MeasureCoord');
    }
  }

  ngOnDestroy(): void {
    this.clearInteraction();
    if (this.mapSubscription) {
      this.mapSubscription.unsubscribe();
    }
    this.basysMapFrameService.map.enableContextMenu(true);
  }

  toggleGeometryType(measureType: 'Point' | 'LineString' | 'Polygon', actionId: string): void {
    this.isMeasureCoordActive = !this.isMeasureCoordActive;
    this.olmap = this.mapViews.olmap;
    this.ensureEditSource();

    this.lineCoordinates = [];
    this.polygonCoordinates = [];

    if (!this.olmap) {
      console.error('OpenLayers map instance is not available.');
      return;
    }

    this.removeExistingInteractions();

    if (this.isMeasureCoordActive) {
      this.activateMeasurement(measureType, actionId);
    } else {
      this.clearInteraction();
    }
  }

  private removeExistingInteractions(): void {
    this.vectorSource?.clear();

    if (this.handleMapClick) {
      this.olmap.un('singleclick', this.handleMapClick);
      this.handleMapClick = null;
    }

    if (this.draw) {
      this.olmap.removeInteraction(this.draw);
      this.draw = null;
    }

    if (this.measureTooltipOverlay) {
      this.olmap.removeOverlay(this.measureTooltipOverlay);
      this.measureTooltipOverlay = null;
    }

    this.vectorSource?.changed();
  }

  private activateMeasurement(measureType: 'Point' | 'LineString' | 'Polygon', actionId: string): void {
    this.draw = new ol.interaction.Draw({
      source: this.vectorSource,
      type: measureType,
      style: this.getDrawStyle(),
    });

    this.olmap.addInteraction(this.draw);
    if (measureType === 'Point') {
      this.handleMapClick = (event: any) => {
        if (!this.isMeasureCoordActive) return;

        const coordinates = event.coordinate;
        this.vectorSource.clear();
        const feature = new ol.Feature({ geometry: new ol.geom.Point(coordinates) });
        this.vectorSource.addFeature(feature);
        this.lastPointFeature = feature;

        this.ngZone.run(() => {
          this.xCoordinate = coordinates[0].toString();
          this.yCoordinate = coordinates[1].toString();
          this.showMissingCoordinatesWarning = false;
          this.updateFabButtonState();
        });

        this.vectorSource.changed();
      };

      this.olmap.on('singleclick', this.handleMapClick);
      this.showMissingCoordinatesWarning = false;
    } else if (measureType === 'LineString') {
      this.enableLineGeometryEdit();
    } else if (measureType === 'Polygon') {
      this.enablePolygonGeometryEdit();
    }
  }

  private enableLineGeometryEdit(): void {
    this.draw.on('drawstart', (event) => {
      this.clearVectorSource();
      this.sketch = event.feature;
      this.listener = this.sketch.getGeometry().on('change', (evt) => {
        const geom = evt.target;
        const length = geom.getLength();
        const output = length >= 1000 ? (length / 1000).toFixed(2) + ' km' : length.toFixed(2) + ' m';

        this.displayMeasurementTooltip(geom, output);
      });
    });

    this.draw.on('drawend', (event) => {
      const finalFeature = event.feature;
      const geom = finalFeature.getGeometry();
      const coordinates = geom?.getCoordinates?.();

      if (Array.isArray(coordinates)) {
        this.lineCoordinates = coordinates;
      } else {
        console.warn('drawend: invalid geometry');
        this.lineCoordinates = [];
      }

      this.isMeasureCoordActive = false;
      this.isLineFinished = true;
      this.hideMeasurementTooltip();
      this.showMissingCoordinatesWarning = false;
      this.updateFabButtonState();
    });
  }

  public clearVectorSource() {
    this.vectorSource?.clear();
    this.vectorSource?.changed();
  }

  private enablePolygonGeometryEdit(): void {
    this.draw.on('drawstart', (event) => {
      this.clearVectorSource();
      this.sketch = event.feature;
      this.listener = this.sketch.getGeometry().on('change', (evt) => {
        const geom = evt.target;
        const area = geom.getArea();
        const output = area >= 1000000 ? (area / 1000000).toFixed(2) + ' km²' : area.toFixed(2) + ' m²';

        this.displayMeasurementTooltip(geom, output);
      });
    });

    this.draw.on('drawend', (event) => {
      const finalFeature = event.feature;
      const geom = finalFeature.getGeometry();
      const coordinates = geom?.getCoordinates?.();

      if (Array.isArray(coordinates) && Array.isArray(coordinates[0])) {
        this.polygonCoordinates = coordinates[0];
      } else {
        this.polygonCoordinates = [];
      }

      this.isMeasureCoordActive = false;
      this.isPolygonFinished = true;
      this.hideMeasurementTooltip();
      this.showMissingCoordinatesWarning = false;
      this.updateFabButtonState();
    });
  }

  private displayMeasurementTooltip(geometry: any, output: string): void {
    if (!this.measureTooltipOverlay) {
      this.measureTooltipOverlay = new ol.Overlay({
        element: document.createElement('div'),
        offset: [0, -15],
        positioning: 'bottom-center',
      });
      this.olmap.addOverlay(this.measureTooltipOverlay);
    }

    this.measureTooltipOverlay.getElement().innerHTML = output;
    this.measureTooltipOverlay.setPosition(geometry.getLastCoordinate());

    this.cd.detectChanges();
  }

  private hideMeasurementTooltip(): void {
    if (this.measureTooltipOverlay) {
      this.measureTooltipOverlay.setPosition(undefined);
    }
    this.sketch = undefined;
    ol.Observable.unByKey(this.listener);
  }

  private getDrawStyle() {
    return new ol.style.Style({
      fill: new ol.style.Fill({
        color: 'rgba(255, 255, 255, 0.2)',
      }),
      stroke: new ol.style.Stroke({
        color: 'rgba(0, 0, 0, 0.5)',
        lineDash: [10, 10],
        width: 2,
      }),
      image: this.getCrossStyle(),
    });
  }

  private getCrossStyle() {
    return new ol.style.RegularShape({
      fill: new ol.style.Fill({
        color: 'red',
      }),
      points: 4,
      radius1: 15,
      radius2: 1,
    });
  }

  private ensureEditSource() {
    if (!this.vectorSource) {
      this.vectorSource = new ol.source.Vector();
    } else {
      this.clearVectorSource();
    }

    this.vectorEditlayer = this.createVectorLayer(this.vectorSource);

    if (this.mapViews && this.mapViews.olmap) {
      this.mapViews.olmap.addLayer(this.vectorEditlayer);
    } else {
      console.error('OpenLayers map instance is not available.');
    }
  }

  private createVectorLayer(src: any): any {
    let vector = new ol.layer.Vector({
      source: this.vectorSource,
      style: new ol.style.Style({
        fill: new ol.style.Fill({
          color: 'rgba(255, 255, 255, 0.2)',
        }),
        stroke: new ol.style.Stroke({
          color: '#ffcc33',
          width: 2,
        }),
        image: new ol.style.Circle({
          radius: 7,
          stroke: new ol.style.Stroke({
            color: 'black',
            width: 1,
          }),
          fill: new ol.style.Fill({
            color: '#ffcc33',
          }),
        }),
      }),
      zIndex: Infinity,
    });

    return vector;
  }

  private centerGeoLocation(): void {
    this.ensureGeoLocationPointFeature();
    const geo = this.geoLocationPointFeature?.getGeometry();

    if (geo) {
      const coords = geo.getCoordinates();
      this.mapViews.setCenter(coords);

      const lonLat = ol.proj.toLonLat(coords, this.olmap.getView().getProjection());

      this.xCoordinate = coords[0].toString();
      this.yCoordinate = coords[1].toString();

      this.vectorSource?.clear();
      const feature = new ol.Feature({ geometry: new ol.geom.Point(coords) });
      this.vectorSource?.addFeature(feature);
      this.lastPointFeature = feature;

      this.vectorSource?.changed();
      this.cd.detectChanges();
    }
  }

  private ensureGeoLocationPointFeature() {
    if (!this.geoLocationPointFeature) {
      this.geoLocationPointFeature = new ol.Feature();

      const source = new ol.source.Vector({
        features: [this.geoLocationPointFeature],
      });

      this.geoLocationPointLayer = new ol.layer.Vector({
        source: source,
        zIndex: 1000,
      });

      this.olmap.addLayer(this.geoLocationPointLayer);
    }
  }

  private clearInteraction(): void {
    if (this.mapViews && this.mapViews.olmap) {
      const olMap = this.mapViews.olmap;

      if (this.handleMapClick) {
        olMap.un('singleclick', this.handleMapClick);
        this.handleMapClick = null;
      }

      if (this.draw) {
        olMap.removeInteraction(this.draw);
        this.draw = null;
      }

      if (this.measureTooltipOverlay) {
        olMap.removeOverlay(this.measureTooltipOverlay);
        this.measureTooltipOverlay = null;
      }
    }
    this.lineCoordinates = [];
    this.polygonCoordinates = [];
  }

  public onXCoordinateChange(newX: string): void {
    if (!this.xCoordinate || !this.yCoordinate) return;

    const updatedX = parseFloat(newX);
    const updatedY = parseFloat(this.yCoordinate);

    if (!isNaN(updatedX) && !isNaN(updatedY)) {
      this.showMissingCoordinatesWarning = false;
      this.updatePointFeature(updatedX, updatedY);
    }
  }

  public onYCoordinateChange(newY: string): void {
    if (!this.xCoordinate || !this.yCoordinate) return;

    const updatedX = parseFloat(this.xCoordinate);
    const updatedY = parseFloat(newY);

    if (!isNaN(updatedX) && !isNaN(updatedY)) {
      this.showMissingCoordinatesWarning = false;
      this.updatePointFeature(updatedX, updatedY);
    }
  }

  private updatePointFeature(newX: number, newY: number): void {
    if (!this.vectorSource) return;

    const features = this.vectorSource.getFeatures();

    let pointFeature = features.find((f) => f.getGeometry().getType() === 'Point');

    if (pointFeature) {
      pointFeature.getGeometry().setCoordinates([newX, newY]);
      pointFeature.changed();

      this.vectorSource.changed();
      this.cd.detectChanges();
    } else {
      console.log('No existing marker found. Cannot move.');
    }
  }

  onToggleFab(): void {
    this.fabTogglerState = this.fabTogglerState === 'inactive' ? 'active' : 'inactive';
  }

  private deleteGeometries() {
    const messageSettings: MessageSettings = new MessageSettings();
    messageSettings.button = MessageBoxButtons.YesNo;
    let textGeometry: string = '';
    switch (this.geometryType) {
      case GeometryEditType.Point:
        textGeometry = this.res.get('GeometryEditor-PointGeometries');
        break;
      case GeometryEditType.Line:
        textGeometry = this.res.get('GeometryEditor-LineGeometries');
        break;
      case GeometryEditType.Polygon:
        textGeometry = this.res.get('GeometryEditor-PolygonGeometries');
        break;
    }

    messageSettings.caption = this.res.get('GeometryEditor-DialogCaption-DeleteGeometry', textGeometry);
    messageSettings.message = this.res.get('GeometryEditor-DialogQuestion-DeleteAllGeometriesOfType', textGeometry);
    messageSettings.icon = MessageBoxIcon.Question;

    this.messageBoxService.showMessageBox(messageSettings).subscribe((dialogResult) => {
      if (dialogResult['result'] === DialogResult.Yes) {
        if (this.geometryType === GeometryEditType.Point) {
          GeometryHelper.deletePointGeometries(this.editGeometry);
        } else if (this.geometryType === GeometryEditType.Line) {
          GeometryHelper.deleteLineGeometries(this.editGeometry);
        } else if (this.geometryType === GeometryEditType.Polygon) {
          GeometryHelper.deletePolygonGeometries(this.editGeometry);
        }

        const clonedGeometry = JSON.parse(JSON.stringify(this.editGeometry));
        this.editGeometry = clonedGeometry;
        this.triggerSave.emit();

        this.handleAfterSaveClick();
        this.closeGeoDialog.emit();
        this.cd.detectChanges();
      }
    });
  }

  private getGeomIdentifier(): string {
    switch (this.geometryType) {
      case GeometryEditType.Line:
        return 'LIN';
      case GeometryEditType.Polygon:
        return 'UMR';
      default:
        return 'PKT';
    }
  }

  saveGeometry() {
    const geomIdentifier = this.getGeomIdentifier();

    const isPointEmpty = this.geometryType === GeometryEditType.Point && !this.lastPointFeature;
    const isLineEmpty = this.geometryType === GeometryEditType.Line && this.lineCoordinates.length < 2;
    const isPolygonEmpty = this.geometryType === GeometryEditType.Polygon && this.polygonCoordinates.length < 3;

    if (isPointEmpty || isLineEmpty || isPolygonEmpty) {
      this.showMissingCoordinatesWarning = true;
      return;
    }

    if (!this.editGeometry) {
      this.editGeometry = {};
    }

    if (this.geometryType === GeometryEditType.Point) {
      const geom = this.lastPointFeature.getGeometry();
      const coordinates = geom.getCoordinates();

      GeometryHelper.addPointToGeometry(this.editGeometry, geomIdentifier, coordinates[0], coordinates[1], 0);
    } else if (this.geometryType === GeometryEditType.Line) {
      if (this.lineCoordinates.length >= 2) {
        const lineFeature = {
          getCoordinates: () => this.lineCoordinates,
        };

        GeometryHelper.addLineToGeometry(this.editGeometry, geomIdentifier, lineFeature);
      } else {
        console.warn('No line coordinates found for save');
      }
    } else if (this.geometryType === GeometryEditType.Polygon) {
      if (this.polygonCoordinates.length >= 3) {
        const polygonFeature = {
          getCoordinates: () => [this.polygonCoordinates],
        };

        GeometryHelper.addPolygonToGeometry(this.editGeometry, geomIdentifier, polygonFeature);
      } else {
        console.warn('No polygon coordinates found for save');
      }
    }

    const clonedGeometry = JSON.parse(JSON.stringify(this.editGeometry));
    this.editGeometry = clonedGeometry;
    this.triggerSave.emit();

    this.handleAfterSaveClick();
    this.closeGeoDialog.emit();
    this.cd.detectChanges();
  }

  public finishDrawing(): void {
    if ((this.draw && this.geometryType === GeometryEditType.Line) || this.geometryType === GeometryEditType.Polygon) {
      this.draw.finishDrawing();
      this.isMeasureCoordActive = false;
      this.isLineFinished = true;
      this.isPolygonFinished = true;

      this.hideMeasurementTooltip();
    }
  }

  private drawGeoLocationPoint(position: { latitude: number; longitude: number; altitude?: number }) {
    this.ensureGeoLocationPointFeature();

    const style = new ol.style.Style({
      image: new ol.style.Circle({
        radius: 6,
        fill: new ol.style.Fill({ color: '#3399CC' }),
        stroke: new ol.style.Stroke({ color: '#fff', width: 2 }),
      }),
    });

    this.geoLocationPointFeature.setStyle(style);

    let coordinates = [position.longitude, position.latitude];
    const proj = this.olmap.getView().getProjection();
    coordinates = ol.proj.fromLonLat(coordinates, proj);

    this.geoLocationPointFeature.setGeometry(new ol.geom.Point(coordinates));
  }
}
